<template>
  <div
    v-if="savingsInPercent"
    :class="[
      'price-savings-in-percent-fi',
      theme ? `price-savings-in-percent-fi--theme-${theme}` : '',
    ]"
  >
    <span class="badge-fi">
      {{ savingsInPercent }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useDict } from '@/composables/fischerPlugin';

interface SavingsInformation {
  savingsInPercent?: string | null;
  [key: string]: any;
}

interface Price {
  savingsInformation?: SavingsInformation | null;
  [key: string]: any;
}

interface PriceRow {
  minQty?: number | null;
  savingsInformation?: SavingsInformation | null;
  active?: boolean;
  [key: string]: any;
}

interface BestPrice {
  price?: Price | null;
  requestedQuantity?: number | null;
  initialQuantity?: number | null;
  priceRowsIncludingSavings?: (PriceRow | null)[] | null;
  [key: string]: any;
}

interface Props {
  bestPrice?: BestPrice | null;
  small?: boolean;
  theme?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  bestPrice: () => ({}),
  small: false,
  theme: null,
});

const dict = useDict();

const quantity = computed(() => {
  let qty = 0;
  if (props.bestPrice) {
    qty =
      (props.bestPrice.requestedQuantity ?? 0) > 0
        ? (props.bestPrice.requestedQuantity ?? 0)
        : (props.bestPrice.initialQuantity ?? 0);
  }
  return qty;
});

const resultPriceRowsSavings = computed<PriceRow[] | null>(() => {
  if (props.bestPrice && props.bestPrice.price) {
    let activeIndex = 0;
    const savings: PriceRow[] = [
      {
        ...props.bestPrice.price,
        minQty: props.bestPrice.initialQuantity ?? 0,
        savingsInformation: null,
      },
      ...(props.bestPrice.priceRowsIncludingSavings || []).filter((r): r is PriceRow => r !== null),
    ];

    savings.forEach((saving, index) => {
      saving.active = false;

      if ((saving.minQty ?? 0) <= quantity.value) {
        activeIndex = index;
      }
    });

    savings[activeIndex].active = true;

    return savings;
  }

  return null;
});

const savingsInPercent = computed(() => {
  if (props.bestPrice?.price?.savingsInformation?.savingsInPercent) {
    return dict?.get('online-Shop.Product.Savings', [
      props.bestPrice.price.savingsInformation.savingsInPercent,
    ]);
  }

  if (resultPriceRowsSavings.value) {
    const activeSaving = resultPriceRowsSavings.value.find((el) => el.active);
    const value = activeSaving?.savingsInformation?.savingsInPercent;
    if (value) {
      return dict?.get('online-Shop.Product.Price-Rows-Saving-Label', [value]);
    }
  }

  return null;
});
</script>

<style scoped src="./price-savings-in-percent-fi" />
